import React, { useEffect } from "react";
import { noop } from "lodash";
import PropTypes from "prop-types";
import Portal from "../../../../components/atoms/Portal";

function FullscreenComponent({
  isFullscreen,
  children,
  className,
  exitFullscreen,
  isNative,
}) {
  useEffect(() => {
    const doc = document;

    function getFullscreenEl() {
      return (
        doc.fullscreenElement ||
        doc.webkitFullscreenElement ||
        doc.mozFullScreenElement ||
        doc.msFullscreenElement
      );
    }

    function exitIfOpen() {
      const el = getFullscreenEl();
      if (!el) {
        exitFullscreen();
      }
    }

    function openFullscreen() {
      const el = doc.body;
      if (!el) return;
      if (el.requestFullscreen) {
        el.requestFullscreen();
      } else if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
      } else if (el.mozRequestFullScreen) {
        el.mozRequestFullScreen();
      } else if (el.msRequestFullscreen) {
        el.msRequestFullscreen();
      } else if (el.webkitEnterFullscreen) {
        el.webkitEnterFullscreen();
      }
    }

    function closeFullscreen() {
      if (doc.exitFullscreen) {
        doc.exitFullscreen();
      } else if (doc.webkitExitFullscreen) {
        doc.webkitExitFullscreen();
      } else if (doc.msExitFullscreen) {
        doc.msExitFullscreen();
      } else if (doc.mozCancelFullScreen) {
        doc.mozCancelFullScreen();
      }
    }

    function registerEvents() {
      doc.addEventListener("fullscreenchange", exitIfOpen);
      doc.addEventListener("mozfullscreenchange", exitIfOpen);
      doc.addEventListener("webkitfullscreenchange", exitIfOpen);
      doc.addEventListener("msfullscreenchange", exitIfOpen);
      return () => {
        doc.removeEventListener("fullscreenchange", exitIfOpen);
        doc.removeEventListener("mozfullscreenchange", exitIfOpen);
        doc.removeEventListener("webkitfullscreenchange", exitIfOpen);
        doc.removeEventListener("msfullscreenchange", exitIfOpen);
      };
    }

    if (isFullscreen && isNative) {
      openFullscreen();
      return registerEvents();
    }
    if (getFullscreenEl() === document.body) {
      closeFullscreen();
      return noop;
    }
    return noop;
  }, [isFullscreen, isNative]);

  useEffect(() => {
    if (isFullscreen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = original;
      };
    }
    return () => {};
  }, [isFullscreen]);

  return isFullscreen ? (
    <Portal>
      <div className={className}>
        <div className="tw-z-9998 tw-fixed tw-inset-0">{children}</div>
      </div>
    </Portal>
  ) : (
    <div className="tw-h-full">{children}</div>
  );
}

FullscreenComponent.propTypes = {
  isFullscreen: PropTypes.bool.isRequired,
  isNative: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string.isRequired,
  exitFullscreen: PropTypes.func.isRequired,
};

export default React.memo(FullscreenComponent);
